import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import type { List } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/Dialog";
import { useLists } from "@/shared/lib/lists";
import { cn } from "@/shared/lib/utils";
import { DeleteListDialog } from "./DeleteListDialog";
import { EditListDialog } from "./EditListDialog";

interface ManageListsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateNew?: () => void;
}

/**
 * Modal list-of-lists. Per docs/features.md, there is no parallel list-detail
 * page — managing lists is a header-driven flow, so a modal is the right
 * surface here (not a route).
 */
export function ManageListsDialog({
  open,
  onOpenChange,
  onCreateNew,
}: ManageListsDialogProps) {
  const lists = useLists();
  const [editing, setEditing] = useState<List | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState<List | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const items = lists.data?.items ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Manage lists</DialogTitle>
            <DialogDescription>
              Rename, update descriptions, or delete the saved scopes for your
              contacts.
            </DialogDescription>
          </DialogHeader>

          {lists.isLoading ? (
            <p className="type-body-base text-text-muted">Loading…</p>
          ) : lists.isError ? (
            <p className="type-body-base text-danger-strong">
              Couldn't load lists: {lists.error.message}
            </p>
          ) : items.length === 0 ? (
            <p className="type-body-base text-text-muted">
              No lists yet. Create one to start scoping your contacts.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {items.map((list) => (
                <li
                  key={list.id}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5",
                    "glass-surface-subtle",
                  )}
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate type-label-base text-text">
                      {list.name}
                    </span>
                    <span className="type-body-sm text-text-muted">
                      {list.memberCount === 1
                        ? "1 member"
                        : `${list.memberCount} members`}
                      {list.description ? ` · ${list.description}` : ""}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${list.name}`}
                    onClick={() => {
                      setEditing(list);
                      setEditOpen(true);
                    }}
                  >
                    <Pencil aria-hidden className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost-danger"
                    size="icon"
                    aria-label={`Delete ${list.name}`}
                    onClick={() => {
                      setDeleting(list);
                      setDeleteOpen(true);
                    }}
                  >
                    <Trash2 aria-hidden className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            {onCreateNew ? (
              <Button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onCreateNew();
                }}
              >
                New list
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditListDialog
        list={editing}
        open={editOpen}
        onOpenChange={(next) => {
          setEditOpen(next);
          if (!next) setEditing(null);
        }}
      />
      <DeleteListDialog
        list={deleting}
        open={deleteOpen}
        onOpenChange={(next) => {
          setDeleteOpen(next);
          if (!next) setDeleting(null);
        }}
      />
    </>
  );
}
